import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  downloadFile, getJSON, patchJSON, postJSON, uploadFile,
} from '../../api/client.js';
import {
  CrudPage, UniActionButton, UniKpiCard, UniPageShell,
  UniSection, UniStatusPill, UniTable, UniTile, fmtCount, useUniList,
} from '../waba/WabaShared.jsx';
import { UniCopyField } from '../uni/UniShared.jsx';
import {
  ChannelBadge, CopyField, CustomShopifyAppCard, EasyField, PixelTestAside, SB_API, SetupCard, ShopifyAppInstallCard, ShopifyConnectCodeCard, THEME, HOME, TechDetails, openShopifyInstallUrl, partnersOauthBlockedInEmbed, formatInTimeZone, useDisplayTimezone, OrderSyncHoursField, DEFAULT_MANUAL_ORDER_SYNC_HOURS, StoreOrderSyncPanel, runStoreOrderSync, OrderSyncStatus,
} from './SignalBridgeShared.jsx';
import { STORE_FIELD_HELP } from './storeFieldHelp.js';
import { BuyerSummary } from './BuyerInsights.jsx';
import {
  invertSortMap, parseOrderingParam, toOrderingParam,
} from '../../lib/tableSort.js';

const P = { theme: THEME, api: SB_API, home: HOME };

function storeShopifyUrl(row) {
  if (!row) return '';
  return row.shopify_webhook_url || row.tenanted_shopify_webhook_url || (row.platform !== 'GOKWIK' ? row.webhook_url : '') || '';
}

function storeGokwikUrl(row) {
  if (!row) return '';
  return row.gokwik_webhook_url || row.tenanted_gokwik_webhook_url || (row.platform === 'GOKWIK' ? row.webhook_url : '') || '';
}

function StoreWebhookCopy({ row, compact }) {
  const shopify = storeShopifyUrl(row);
  const gokwik = storeGokwikUrl(row);
  if (!shopify && !gokwik) return compact ? '—' : null;
  return (
    <div style={{ display: 'grid', gap: compact ? 8 : 12 }}>
      {shopify ? (
        <UniCopyField
          compact={!!compact}
          label="Shopify webhook URL"
          value={shopify}
          open={false}
          hint={compact ? undefined : 'Copy into the Shopify script, or use Register webhooks. Shopify signs this URL.'}
        />
      ) : null}
      {gokwik ? (
        <UniCopyField
          compact={!!compact}
          label="GoKwik webhook URL"
          value={gokwik}
          open={false}
          hint={compact ? undefined : 'Copy into GoKwik Dashboard → Kwik Checkout → Settings → Integrations → Webhooks. Same URL for Abandoned Cart (Address Added) and Payment Webhooks V3. No auth header.'}
        />
      ) : null}
    </div>
  );
}

const EVENT_SORT_FIELDS = {
  event_id: 'event_id',
  shopify_order_number: 'order_number',
  event_time: 'event_time',
  event_type: 'event_type',
  payment_type: 'payment_type',
  value: 'value',
  status: 'status',
  attempts: 'attempts',
  events_received_count: 'events_received_count',
  sent_at: 'sent_at',
  source: 'incoming_event__source',
};
const EVENT_ORDER_TO_COL = invertSortMap(EVENT_SORT_FIELDS);

const PAYLOAD_SORT_FIELDS = {
  created_at: 'created_at',
  event_type: 'outbound_event__event_type',
  event_id: 'outbound_event__event_id',
  pixel_name: 'outbound_event__pixel_account__name',
  store_domain: 'outbound_event__incoming_event__store_connection__store_domain',
  source: 'outbound_event__incoming_event__source',
  status: 'status',
};
const PAYLOAD_ORDER_TO_COL = invertSortMap(PAYLOAD_SORT_FIELDS);

function headerSortFromOrdering(ordering, orderToCol) {
  const parsed = parseOrderingParam(ordering);
  if (!parsed.key) return { key: null, dir: 'asc' };
  return { key: orderToCol[parsed.key] || null, dir: parsed.dir };
}

const EVENT_BAR_COLORS = {
  PageView: '#94a3b8',
  ViewContent: '#38bdf8',
  AddToCart: '#f59e0b',
  InitiateCheckout: '#a78bfa',
  Purchase: '#1877f2',
};

function displayShopifyOrderNumber(row) {
  const n = String((row && row.shopify_order_number) || '').trim();
  return n || '—';
}

function prettyJson(value) {
  try {
    const text = JSON.stringify(value, null, 2);
    return text == null ? String(value) : text;
  } catch (err) {
    return String(value);
  }
}

function payloadInspectJson(row) {
  if (!row) return '(No JSON stored for this send.)';
  const hasRequest = row.request_payload != null && row.request_payload !== '';
  const hasResponse = row.response_body != null && row.response_body !== '';
  const hasError = Boolean(row.error_detail);
  if (!hasRequest && !hasResponse && !hasError) {
    return '(No JSON stored for this send.)';
  }
  return prettyJson({
    request: row.request_payload ?? null,
    response: row.response_body ?? null,
    error: row.error_detail ?? null,
  });
}

function qualityWarningList(warnings) {
  if (!Array.isArray(warnings) || !warnings.length) return null;
  return (
    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
      {warnings.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

function MetaVisibilityNotice({ visibility }) {
  if (!visibility) return null;
  const failed = visibility.ok === false;
  const message = visibility.message || visibility.where_to_look;
  if (!message && !(visibility.quality_warnings || []).length) return null;
  return (
    <div style={{ margin: '8px 0 16px' }}>
      <div className={failed ? 'ty-error' : 'ty-ok'} role="status">
        <div className="ty-error__head">
          <span className="ty-error__icon" aria-hidden="true">{failed ? '⚠️' : '✓'}</span>
          {String(message || '')}
        </div>
      </div>
      {visibility.where_to_look && visibility.where_to_look !== message ? (
        <p className="uni-num--muted" style={{ marginTop: 8 }}>{visibility.where_to_look}</p>
      ) : null}
      {qualityWarningList(visibility.quality_warnings)}
    </div>
  );
}

function isGokwikEvent(raw) {
  if (!raw) return false;
  if (raw.store_platform === 'GOKWIK' || raw.source === 'GOKWIK_WEBHOOK') return true;
  return String(raw.event_id || '').includes('_gokwik_');
}

function SendToMetaResult({ sendOut }) {
  if (!sendOut) return null;
  const result = sendOut.send_result || null;
  const failed = sendOut.ok === false || Boolean(sendOut.error && sendOut.ok !== true);
  const ok = !failed;
  const message = sendOut.message || sendOut.hint || sendOut.error
    || (ok ? 'This event reached Meta.' : 'This event did not reach Meta.');
  const hasMetaLine = result && (
    result.http_status != null || result.events_received != null || result.trace_id || result.status
  );
  const response = result && (result.response != null ? result.response : null);
  const warnings = (result && result.quality_warnings) || sendOut.quality_warnings || [];
  const where = (result && result.where_to_look) || sendOut.where_to_look;
  return (
    <div style={{ margin: '8px 0 16px' }}>
      <div className={ok ? 'ty-ok' : 'ty-error'} role="status">
        <div className="ty-error__head">
          <span className="ty-error__icon" aria-hidden="true">{ok ? '✓' : '⚠️'}</span>
          {String(message)}
        </div>
      </div>
      {hasMetaLine && (
        <p className="uni-num--muted" style={{ marginTop: 8 }}>
          {result.http_status != null ? `HTTP ${result.http_status}` : 'No HTTP status'}
          {result.events_received != null ? ` · events_received ${result.events_received}` : ''}
          {result.trace_id ? ` · fbtrace ${result.trace_id}` : ''}
          {result.status ? ` · ${result.status}` : ''}
          {result.pixel_id ? ` · pixel ${result.pixel_id}` : ''}
        </p>
      )}
      {where ? <p className="uni-num--muted" style={{ marginTop: 8 }}>{where}</p> : null}
      {qualityWarningList(warnings)}
      {response != null && (
        <>
          <UniSection title="This send — Meta response" />
          <pre className="uni-json">{prettyJson(response)}</pre>
        </>
      )}
    </div>
  );
}

function DailyBars({ daily }) {
  const rows = daily || [];
  const types = Object.keys(EVENT_BAR_COLORS);
  const max = Math.max(1, ...rows.map((d) => Number(d.total) || 0));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, padding: '8px 0' }}>
        {rows.map((d) => (
          <div key={d.date} style={{ flex: 1, textAlign: 'center' }} title={`${d.date}: ${d.total}`}>
            <div
              style={{
                height: `${Math.round(((Number(d.total) || 0) / max) * 110)}px`,
                minHeight: 2,
                display: 'flex',
                flexDirection: 'column-reverse',
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(24,119,242,0.08)',
              }}
            >
              {types.map((t) => {
                const n = Number(d[t]) || 0;
                if (!n) return null;
                return (
                  <div
                    key={t}
                    style={{
                      height: `${Math.round((n / Math.max(Number(d.total) || 1, 1)) * 100)}%`,
                      background: EVENT_BAR_COLORS[t],
                      minHeight: n ? 2 : 0,
                    }}
                    title={`${t}: ${n}`}
                  />
                );
              })}
            </div>
            <div className="uni-num--muted" style={{ fontSize: 10, marginTop: 4 }}>
              {String(d.date).slice(5)}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#555' }}>
        {types.map((t) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: EVENT_BAR_COLORS[t], display: 'inline-block' }} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SignalBridgeDashboard() {
  const summary = useUniList(`${SB_API}/dashboard/`);
  const s = summary.raw || {};
  const t = s.today || {};
  const w = s.days_7 || {};
  const setup = s.setup || {};
  const live = setup.live || {};
  const stores = setup.stores || [];
  const v = (n) => (summary.loading ? '…' : fmtCount(n));
  const rate = w.delivery_rate_pct == null ? '—' : `${w.delivery_rate_pct}%`;
  const storeDone = !!live.store;
  const adsDone = !!(setup.meta_connected || setup.google_connected);
  const trackingDone = !!live.verify;
  const next = !storeDone ? 1 : (!adsDone ? 2 : (!trackingDone ? 3 : 0));
  const [advanced, setAdvanced] = React.useState(false);
  const tz = useDisplayTimezone();
  const fmtWhen = (iso) => {
    if (!iso) return 'not yet';
    const shown = formatInTimeZone(iso, tz);
    return shown === '—' ? 'not yet' : shown;
  };

  return (
    <UniPageShell
      theme={THEME}
      icon="◎"
      title="Stop guessing which ads sell"
      subtitle="Connect your store once. SignalBridge captures visits and purchases and pushes them into Meta and Google — no code."
      helpPage="dashboard"
      helpApi={SB_API}
      helpOpen={false}
      actions={(
        <>
          <UniActionButton hero primary to={storeDone ? '/signalbridge/connect-pixel' : '/signalbridge/connect-store'}>
            {setup.ready ? 'Add a channel' : 'Get started'}
          </UniActionButton>
          <UniActionButton hero to="/signalbridge/test-event">Send a test</UniActionButton>
        </>
      )}
    >
      <div className="sb-trust">
        <span>Set up in minutes</span>
        <span>No coding required</span>
        <span>Numbers land in your ads manager</span>
      </div>

      {!storeDone ? (
        <>
          <ShopifyAppInstallCard />
          <ShopifyConnectCodeCard later />
          <CustomShopifyAppCard onConnected={() => window.location.reload()} />
        </>
      ) : null}

      {setup.tracking ? (
        <div className="sb-live">
          <div>
            <p className="sb-live__title">Tracking is live</p>
            <p className="sb-live__meta">
              Last store event {fmtWhen(live.last_received_at)} · last send to ads {fmtWhen(live.last_sent_at)}
            </p>
          </div>
          <UniActionButton primary to="/signalbridge/events">Open event log</UniActionButton>
        </div>
      ) : (
        <div className="sb-setup">
          <SetupCard
            n={1}
            done={storeDone}
            next={next === 1}
            to="/signalbridge/connect-store"
            title="Connect your store"
            sub="Install SignalBridge on Shopify, or GoKwik. We listen for orders so you never paste theme code."
            cta="Connect store →"
          />
          <SetupCard
            n={2}
            done={adsDone}
            next={next === 2}
            to="/signalbridge/connect-pixel"
            title="Connect datasets"
            sub="Add your Meta dataset ID and token. Optional: Google Ads conversion id. Toggle more destinations anytime."
            cta="Add dataset →"
          />
          <SetupCard
            n={3}
            done={trackingDone}
            next={next === 3}
            to="/signalbridge/test-event"
            title="Tracking runs on autopilot"
            sub="Place a test order or send a test event. After that, visits and sales flow into your ads manager by themselves."
            cta="Send a test event →"
          />
        </div>
      )}

      <UniSection title="This week" />
      <div className="uni-kpis">
        <UniKpiCard icon="◎" label="Events today" value={v(t.total)} to="/signalbridge/events" />
        <UniKpiCard icon="₹" label="Purchases (7d)" value={v(w.purchases ?? ((w.prepaid || 0) + (w.cod || 0)))} />
        <UniKpiCard icon="✓" label="Sent to ads (7d)" value={summary.loading ? '…' : rate} />
        <UniKpiCard icon="!" label="Needs attention (7d)" value={v((w.failed || 0) + (w.rejected || 0))} to="/signalbridge/events" />
      </div>

      <BuyerSummary buyer={s.buyer} loading={summary.loading} error={summary.error} />

      <UniSection title="Daily volume (14 days)" />
      <DailyBars daily={s.daily} />

      <UniSection title="Your stores" />
      {summary.loading ? (
        <div className="uni-state">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="sb-empty">
          <h3>No store connected yet</h3>
          <p>Start with Shopify or GoKwik. You can add Meta and Google on the next step.</p>
          <UniActionButton primary to="/signalbridge/connect-store">Connect a store</UniActionButton>
        </div>
      ) : (
        <div className="sb-stores">
          {stores.map((store) => (
            <div key={store.id} className="sb-store">
              <div className="sb-store__head">
                <div>
                  <p className="sb-store__name">{store.store_domain}</p>
                  <UniStatusPill value={store.platform} />
                </div>
                <UniStatusPill value={store.is_active ? 'active' : 'inactive'} />
              </div>
              <div className="sb-channels">
                <ChannelBadge on={!!store.pixel_name} label={store.pixel_name ? `Meta · ${store.pixel_name}` : 'Meta'} />
                <ChannelBadge on={!!setup.google_connected} label="Google Ads" />
              </div>
              <StoreWebhookCopy row={store} />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '-8px 0 22px' }}>
        <UniActionButton to="/signalbridge/stores">Manage stores</UniActionButton>
        <UniActionButton to="/signalbridge/connect-pixel">Add dataset</UniActionButton>
        <UniActionButton to="/signalbridge/google-ads">Add Google Ads</UniActionButton>
      </div>

      {(s.recent_failures || []).length > 0 && (
        <>
          <UniSection title="Needs a retry" />
          <UniTable
            rows={s.recent_failures || []}
            loading={summary.loading}
            empty="All recent events delivered"
            rowKey="id"
            columns={[
              { key: 'event_id', label: 'Event', render: (r) => <Link to={`/signalbridge/events/${r.id}`}>{r.event_id}</Link> },
              { key: 'event_type', label: 'Type' },
              { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
              {
                key: 'retry',
                label: '',
                align: 'right',
                render: (r) => (r.status === 'FAILED'
                  ? <RetryButton id={r.id} onDone={() => summary.reload && summary.reload()} />
                  : null),
              },
            ]}
          />
        </>
      )}

      <div className="sb-advanced">
        <button type="button" className="sb-advanced__toggle" onClick={() => setAdvanced((o) => !o)}>
          {advanced ? 'Hide advanced tools' : 'Show advanced tools'}
        </button>
        {advanced && (
          <div className="sb-advanced__body">
            <div className="sb-advanced__group">
              <h3>Everyday</h3>
              <div className="uni-tiles">
                <UniTile to="/signalbridge/events" icon="📋" title="Event log" sub="What reached Meta and Google" />
                <UniTile to="/signalbridge/templates" icon="⬡" title="Templates" sub="Payload templates sent to Meta CAPI" />
                <UniTile to="/signalbridge/payloads" icon="◈" title="Meta payloads" sub="JSON sent to Meta, filter and sort" />
                <UniTile to="/signalbridge/upload" icon="⬆" title="Upload CSV" sub="Backfill sales from a spreadsheet" />
                <UniTile to="/signalbridge/test-event" icon="⚗" title="Test event" sub="Confirm Events Manager sees a test" />
                <UniTile to="/signalbridge/purchase-report" icon="▤" title="Purchase report" sub="Entry point, device, state, campaign" />
                <UniTile to="/signalbridge/reports" icon="▤" title="Reports" sub="Funnel, COD, UTM, coverage" />
                <UniTile to="/signalbridge/collect" icon="◎" title="Browser collect" sub="Shared collector plus a per-store config" />
              </div>
            </div>
            <div className="sb-advanced__group">
              <h3>Fine-tune</h3>
              <div className="uni-tiles">
                <UniTile to="/signalbridge/configure-events" icon="☑" title="Which events to send" sub="Pick Purchase, AddToCart, and the fields on each" />
                <UniTile to="/signalbridge/parameters" icon="☑" title="Parameter matrix" sub="Power-user checkboxes per event type" />
                <UniTile to="/signalbridge/pixel-groups" icon="▦" title="Pixel groups" sub="Fan one sale to several Meta pixels" />
                <UniTile to="/signalbridge/routes" icon="⤳" title="Event routes" sub="Filters → destinations" />
                <UniTile to="/signalbridge/destinations" icon="↗" title="Destinations" sub="Meta, Google, or a webhook" />
                <UniTile to="/signalbridge/merge-setup" icon="⚭" title="Shopify + GoKwik merge" sub="One event when checkout is split" />
              </div>
            </div>
            <div className="sb-advanced__group">
              <h3>Ops</h3>
              <div className="uni-tiles">
                <UniTile to="/signalbridge/journey" icon="↕" title="Order journey" sub="Inbound → outbound → attempts" />
                <UniTile to="/signalbridge/dlq" icon="📭" title="Failed incoming" sub="Replay by fingerprint" />
                <UniTile to="/signalbridge/resend" icon="↻" title="Historical resend" sub="Replay recent events (Meta skips >7 days)" />
                <UniTile to="/signalbridge/compare" icon="▤" title="Pixel comparison" sub="Same event_id across destinations" />
                <UniTile to="/signalbridge/alerts" icon="!" title="Alerts" sub="Email / SMS when tracking drops" />
                <UniTile to="/signalbridge/merge" icon="⚭" title="Merge browser" sub="Wait times and held pairs" />
                <UniTile to="/signalbridge/settings" icon="⚙" title="Settings" sub="Retention, retry, health thresholds" />
                <UniTile to="/signalbridge/audit" icon="☰" title="Audit" sub="Who changed tokens and routes" />
                <UniTile to="/signalbridge/help" icon="❓" title="Help" sub="Same copy as the banners" />
              </div>
            </div>
          </div>
        )}
      </div>
    </UniPageShell>
  );
}

function RetryButton({ id, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await postJSON(`${SB_API}/events/${id}/retry/`);
      if (onDone) onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="ty-btn ty-btn--secondary" disabled={busy} onClick={run}>
      {busy ? '…' : 'Retry'}
    </button>
  );
}

export function SignalBridgePixels() {
  const [selected, setSelected] = React.useState(null);
  return (
    <CrudPage
      {...P} helpPage="pixels" icon="◈" title="Datasets"
      subtitle="Add a Meta dataset the way you would in Events Manager — Dataset ID (Pixel ID) plus access token. Test CAPI from the panel on the right; it does not edit the dataset."
      helpOpen={false}
      endpoint={`${SB_API}/pixels/`}
      searchPlaceholder="Search name or dataset id…"
      createLabel="+ Dataset"
      extraActions={(
        <>
          <UniActionButton hero to="/signalbridge/templates">Templates</UniActionButton>
          <UniActionButton hero to="/signalbridge/payloads">Meta payloads</UniActionButton>
          <UniActionButton hero to="/signalbridge/connect-pixel">Guided setup</UniActionButton>
          <UniActionButton hero to="/signalbridge/google-ads">Add Google Ads</UniActionButton>
        </>
      )}
      empty="No dataset yet"
      emptyHint="Add a Dataset ID and access token from Events Manager. About a minute."
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'pixel_id', label: 'Dataset ID', required: true, hint: 'Numeric Dataset ID (Pixel ID) from Events Manager. Each ID can only be added once.' },
        { key: 'access_token', label: 'CAPI access token', type: 'password', hint: 'Encrypted at rest. Leave blank on edit to keep.' },
        { key: 'api_version', label: 'API version', hint: 'Default v21.0' },
        { key: 'default_event_source_url', label: 'Default event_source_url', hint: 'Fallback shop URL; never invented per event' },
        { key: 'pixel_group_id', label: 'Pixel group id (optional)', hint: 'Adds this pixel’s Meta destination to an existing group' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
      columns={[
        { key: 'name', label: 'Name', render: (r) => <strong>{r.name}</strong> },
        { key: 'pixel_id', label: 'Dataset ID' },
        { key: 'api_version', label: 'API' },
        { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        { key: 'health_status', label: 'Health', render: (r) => <UniStatusPill value={r.health_status || 'UNKNOWN'} /> },
        { key: 'access_token_set', label: 'Token', render: (r) => (r.access_token_set ? 'set' : 'missing') },
      ]}
      extraRowActions={(r) => ([
        { key: 'wizard', label: 'Edit in wizard', to: `/signalbridge/connect-pixel?pixel=${r.id}` },
        {
          key: 'test',
          label: selected?.id === r.id ? 'Testing this pixel →' : 'Test…',
          onClick: () => setSelected(r),
        },
      ])}
      aside={(
        <PixelTestAside
          pixelPk={selected?.id || ''}
          initialCode={selected?.test_event_code || ''}
          canAttempt={!!selected}
          blockedReason="Click Test… on a pixel row. Paste a Test Events code here — the pixel record is not edited."
        />
      )}
    />
  );
}

export function SignalBridgeStores() {
  const pixels = useUniList(`${SB_API}/pixels/`);
  const opts = (pixels.rows || []).map((p) => ({ value: p.id, label: p.name }));
  const [reg, setReg] = React.useState(null);
  const [syncOut, setSyncOut] = React.useState(null);
  const [storeBusy, setStoreBusy] = React.useState('');
  const [syncHours, setSyncHours] = React.useState(DEFAULT_MANUAL_ORDER_SYNC_HOURS);
  const search = (() => {
    try {
      return new URLSearchParams(window.location.search);
    } catch (err) {
      return new URLSearchParams();
    }
  })();
  const [oauthShop, setOauthShop] = React.useState(() => search.get('shop') || '');
  const [oauthMsg, setOauthMsg] = React.useState('');
  const [createdStore, setCreatedStore] = React.useState(null);
  const registerHooks = async (row) => {
    setStoreBusy(`reg-${row.id}`);
    try {
      const data = await postJSON(`${SB_API}/stores/${row.id}/register-webhooks/`);
      setReg(data);
    } catch (err) {
      setReg({ error: String(err.message || err) });
    } finally {
      setStoreBusy('');
    }
  };
  const syncOrders = async (row) => {
    setStoreBusy(`sync-${row.id}`);
    setSyncOut({
      ok: true,
      started: true,
      status: 'running',
      message: 'Starting order sync…',
    });
    try {
      const data = await runStoreOrderSync(row.id, syncHours, { onProgress: setSyncOut });
      setSyncOut(data);
    } catch (err) {
      const data = err && err.data;
      setSyncOut({
        ok: false,
        error: String(err.message || err),
        detail: data && (data.detail || data.message),
        ...((data && typeof data === 'object') ? data : {}),
      });
    } finally {
      setStoreBusy('');
    }
  };
  const connectShopifyApp = async () => {
    setOauthMsg('');
    if (partnersOauthBlockedInEmbed()) {
      setOauthMsg(
        'Partners OAuth cannot run inside this custom-app embed. Use Client ID and Client secret below.',
      );
      return;
    }
    try {
      const q = oauthShop ? `?shop=${encodeURIComponent(oauthShop)}` : '';
      const data = await getJSON(`${SB_API}/shopify-app/install-url/${q}`);
      if (!data.configured || !data.install_url) {
        setOauthMsg(data.detail || 'Shopify app install is not configured on this organisation.');
        return;
      }
      openShopifyInstallUrl(data.install_url);
    } catch (err) {
      setOauthMsg(String(err.message || err));
    }
  };
  return (
    <CrudPage
      {...P} helpPage="stores" icon="🏪" title="Stores"
      subtitle="Install the Partners app, connect a store-owned custom app, or receive GoKwik webhooks."
      helpOpen={false}
      endpoint={`${SB_API}/stores/`}
      searchPlaceholder="Search domain…"
      createLabel="+ Store"
      empty="No store yet"
      emptyHint="Connect Shopify or GoKwik in a couple of minutes — no theme code."
      extraActions={<UniActionButton hero to="/signalbridge/connect-store">Guided setup</UniActionButton>}
      initialCreate={{ is_active: true }}
      onCreated={(row) => setCreatedStore(row)}
      lead={(
        <>
          <ShopifyAppInstallCard />
          <ShopifyConnectCodeCard
            later
            autoRedeem
            onConnected={(data) => {
              if (data && data.shop) setOauthShop(data.shop);
            }}
          />
          <CustomShopifyAppCard
            onConnected={(data) => {
              if (data && data.store_domain) setOauthShop(data.store_domain);
              window.location.reload();
            }}
          />
          <details className="sb-connect-later">
            <summary>Install from a specific myshopify domain</summary>
            <p className="uni-num--muted" style={{ marginTop: 12 }}>
              One-click install already uses this workspace. Only fill this if Shopify
              needs the exact shop host (example: a00cny-n6.myshopify.com).
            </p>
            <EasyField label="Shopify domain" hint="your-store.myshopify.com">
              <input
                placeholder="a00cny-n6.myshopify.com"
                value={oauthShop}
                onChange={(e) => setOauthShop(e.target.value)}
              />
            </EasyField>
            <UniActionButton hero onClick={connectShopifyApp}>
              Connect this domain
            </UniActionButton>
            {oauthMsg ? <p className="uni-num--muted">{oauthMsg}</p> : null}
          </details>
          <div className="uni-note" style={{ marginTop: 16 }}>
            <h3 style={{ margin: '0 0 8px' }}>GoKwik checkout</h3>
            <p className="uni-num--muted" style={{ margin: 0 }}>
              GoKwik sends orders, abandoned carts, and payment events with webhooks. On the store row, copy the GoKwik webhook URL
              (not the Shopify one) into GoKwik Dashboard → Kwik Checkout → Settings → Integrations → Webhooks.
              Enable order webhooks and Abandoned Cart Webhooks (cart state: Address Added). Paste that same URL into Payment
              Webhooks V3 and subscribe to transaction.successful — a successful payment is a confirmed Purchase sent to Meta.
              Abandoned carts become InitiateCheckout in Event log, Meta CAPI, and Reports → ABANDON_CART.
              Paste App ID + App Secret (Dashboard → Account) if you also want SignalBridge to pull missed orders.
            </p>
          </div>
        </>
      )}
      tail={(
        <div style={{ margin: '16px 0 24px' }}>
          <OrderSyncHoursField value={syncHours} onChange={setSyncHours} />
          {createdStore && (storeShopifyUrl(createdStore) || storeGokwikUrl(createdStore)) ? (
            <div className="uni-note" style={{ marginBottom: 16, marginTop: 16 }}>
              <h3 style={{ margin: '0 0 8px' }}>Webhook URLs</h3>
              <p className="uni-num--muted" style={{ margin: '0 0 8px' }}>
                Copy the Shopify URL into the Shopify script. Copy the GoKwik URL into GoKwik. They are different paths.
              </p>
              <StoreWebhookCopy row={createdStore} />
            </div>
          ) : null}
          {reg ? (
            <>
              <p>{reg.error ? 'Could not register webhooks automatically — copy the URL into Shopify instead.' : 'Webhooks registered on the store.'}</p>
              <TechDetails title="Shopify webhook response" data={reg} />
            </>
          ) : null}
          {syncOut ? (
            <>
              <OrderSyncStatus data={syncOut} />
              <p className="uni-num--muted" style={{ marginTop: 8 }}>
                Open <Link to="/signalbridge/events">Event log</Link> and search by Shopify order number
                (for example #3210). “Already in the Event log” means those recent orders were stored
                earlier (webhooks or a previous sync), not that older Shopify orders were imported.
              </p>
            </>
          ) : null}
        </div>
      )}
      fields={[
          { key: 'pixel_account', label: 'Dataset (optional for Google-only)', type: 'select', options: opts, help: STORE_FIELD_HELP.pixel_account },
          { key: 'platform', label: 'Platform', type: 'select', required: true, options: [
            { value: 'SHOPIFY', label: 'Shopify' },
            { value: 'GOKWIK', label: 'GoKwik' },
          ], help: STORE_FIELD_HELP.platform },
          { key: 'store_domain', label: 'Store domain', required: true, hint: 'janayush.myshopify.com', help: STORE_FIELD_HELP.store_domain },
          { key: 'public_domain', label: 'Public domain', required: true, hint: 'https://janayush.com — event_source_url fallback', help: STORE_FIELD_HELP.public_domain },
          {
            key: 'shopify_webhook_url',
            type: 'copy',
            editable: false,
            label: 'Shopify webhook URL',
            hint: 'Copy into the Shopify script, or use Register webhooks. Shopify signs this URL.',
            help: STORE_FIELD_HELP.shopify_webhook_url,
            when: (row) => Boolean(row?.shopify_webhook_url || row?.tenanted_shopify_webhook_url),
          },
          {
            key: 'gokwik_webhook_url',
            type: 'copy',
            editable: false,
            label: 'GoKwik webhook URL',
            hint: 'Paste into GoKwik Dashboard → Kwik Checkout → Settings → Integrations → Webhooks. Same URL for Abandoned Cart (Address Added) and Payment Webhooks V3.',
            help: STORE_FIELD_HELP.gokwik_webhook_url,
            when: (row) => Boolean(row?.gokwik_webhook_url || row?.tenanted_gokwik_webhook_url),
          },
          { key: 'webhook_secret', label: 'Webhook HMAC secret', type: 'password', hint: 'Shopify client secret or GoKwik HMAC secret', help: STORE_FIELD_HELP.webhook_secret },
          { key: 'gokwik_app_id', label: 'GoKwik App ID', hint: 'Dashboard → Account. Needed to pull orders, not for webhooks.', help: STORE_FIELD_HELP.gokwik_app_id },
          { key: 'api_access_token', label: 'Shopify Admin token / GoKwik App Secret', type: 'password', hint: 'Shopify Admin token, or GoKwik App Secret to sync orders', help: STORE_FIELD_HELP.api_access_token },
          { key: 'auth_mode', label: 'Auth mode (GoKwik)', type: 'select', options: [
            { value: 'HMAC', label: 'HMAC' },
            { value: 'SHARED_TOKEN', label: 'Shared token' },
            { value: 'IP_ALLOWLIST', label: 'IP allow-list' },
          ], help: STORE_FIELD_HELP.auth_mode },
          { key: 'shared_token', label: 'Shared token', type: 'password', hint: 'Only when GoKwik is not using HMAC', help: STORE_FIELD_HELP.shared_token },
          { key: 'is_active', label: 'Active', type: 'check', help: STORE_FIELD_HELP.is_active },
        ]}
        columns={[
          { key: 'store_domain', label: 'Store' },
          { key: 'platform', label: 'Platform', render: (r) => <UniStatusPill value={r.platform} /> },
          { key: 'pixel_name', label: 'Pixel' },
          { key: 'health_status', label: 'Health', render: (r) => <UniStatusPill value={r.health_status || 'UNKNOWN'} /> },
          { key: 'webhook', label: 'Webhook URL', sortable: false, render: (r) => (
            <StoreWebhookCopy row={r} compact />
          ) },
        ]}
        extraRowActions={(r) => ([
          { key: 'wizard', label: 'Edit in wizard', to: `/signalbridge/connect-store?store=${r.id}` },
          ...(r.platform === 'SHOPIFY' && r.api_access_token_set
            ? [
              {
                key: 'reg',
                label: 'Register webhooks',
                busy: storeBusy === `reg-${r.id}`,
                onClick: () => registerHooks(r),
              },
              {
                key: 'sync',
                label: 'Sync orders from Shopify',
                busy: storeBusy === `sync-${r.id}`,
                onClick: () => syncOrders(r),
              },
            ]
            : []),
          ...(r.platform === 'GOKWIK' && r.gokwik_app_id_set && r.api_access_token_set
            ? [
              {
                key: 'sync',
                label: 'Sync orders from GoKwik',
                busy: storeBusy === `sync-${r.id}`,
                onClick: () => syncOrders(r),
              },
            ]
            : []),
        ])}
    />
  );
}

export function SignalBridgeParameters() {
  const pixels = useUniList(`${SB_API}/pixels/`);
  const [pixel, setPixel] = React.useState('');
  const [eventType, setEventType] = React.useState('Purchase');
  const [matrix, setMatrix] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    if (!pixel && pixels.rows?.[0]) setPixel(pixels.rows[0].id);
  }, [pixels.rows, pixel]);

  const load = React.useCallback(() => {
    if (!pixel) return;
    getJSON(`${SB_API}/parameters/?pixel=${pixel}`)
      .then(setMatrix)
      .catch(() => setMatrix(null));
  }, [pixel]);
  React.useEffect(load, [load]);

  const toggle = (param, et, checked) => {
    if (!matrix || param.always_on) return;
    const cell = param.by_event?.[et];
    if (!cell) return;
    setMatrix((m) => ({
      ...m,
      parameters: m.parameters.map((p) => (
        p.param_key === param.param_key
          ? { ...p, by_event: { ...p.by_event, [et]: { ...p.by_event[et], is_enabled: checked } } }
          : p
      )),
    }));
  };

  const save = async () => {
    if (!matrix) return;
    const updates = [];
    matrix.parameters.forEach((p) => {
      Object.values(p.by_event || {}).forEach((cell) => {
        if (cell?.id) updates.push({ id: cell.id, is_enabled: !!cell.is_enabled });
      });
    });
    setBusy(true);
    setMsg('');
    try {
      await patchJSON(`${SB_API}/parameters/bulk/`, { updates });
      setMsg('Saved.');
      load();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    if (!pixel) return;
    const enabled = (matrix?.parameters || [])
      .filter((p) => p.always_on || p.by_event?.[eventType]?.is_enabled)
      .map((p) => p.param_key);
    setBusy(true);
    try {
      const data = await postJSON(`${SB_API}/pixels/${pixel}/preview-payload/`, {
        event_type: eventType,
        enabled_keys: enabled,
      });
      setPreview(data);
    } catch (err) {
      setPreview({ error: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const groups = ['USER_DATA', 'CUSTOM_DATA', 'META'];
  const eventTypes = matrix?.event_types || ['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'];

  return (
    <UniPageShell
      {...P} icon="☑" title="Parameter configuration" helpPage="parameters"
      subtitle="A parameter is sent only when it is enabled AND present in the source. event_id, event_time and action_source are always on."
      actions={(
        <>
          <UniActionButton hero primary onClick={save}>{busy ? 'Saving…' : 'Save'}</UniActionButton>
          <UniActionButton hero onClick={doPreview}>Preview payload</UniActionButton>
          <UniActionButton hero to="/signalbridge/configure-events">Configure Events (wizard)</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label>Pixel{' '}
          <select value={pixel} onChange={(e) => setPixel(e.target.value)}>
            {(pixels.rows || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Preview as{' '}
          <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
            {eventTypes.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
        {msg && <span>{msg}</span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="uni-table" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th>Parameter</th>
              {eventTypes.map((e) => <th key={e} style={{ textAlign: 'center' }}>{e}</th>)}
              <th>Seen in source</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g}>
                <tr><td colSpan={eventTypes.length + 2}><strong>{g}</strong></td></tr>
                {(matrix?.parameters || []).filter((p) => p.param_group === g).map((p) => (
                  <tr key={p.param_key}>
                    <td>
                      <code>{p.param_key}</code>
                      <div className="uni-num--muted" style={{ fontSize: 12 }}>{p.description}</div>
                    </td>
                    {eventTypes.map((e) => {
                      const cell = p.by_event?.[e];
                      const checked = !!(p.always_on || cell?.is_enabled);
                      return (
                        <td key={e} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={p.always_on}
                            onChange={(ev) => toggle(p, e, ev.target.checked)}
                          />
                        </td>
                      );
                    })}
                    <td>
                      <UniStatusPill value={p.seen_in_source ? 'received' : 'not seen'} />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {preview && (
        <pre className="uni-note" style={{ marginTop: 16, whiteSpace: 'pre-wrap', fontSize: 12 }}>
          {JSON.stringify(preview, null, 2)}
        </pre>
      )}
    </UniPageShell>
  );
}

export function TestEventForm({ onSent }) {
  const pixels = useUniList(`${SB_API}/pixels/`);
  const incoming = useUniList(`${SB_API}/incoming/?page_size=20`);
  const [pixel, setPixel] = React.useState('');
  const [eventType, setEventType] = React.useState('Purchase');
  const [incomingId, setIncomingId] = React.useState('');
  const [testCode, setTestCode] = React.useState('');
  const [fields, setFields] = React.useState({ email: '', phone: '', value: '', currency: 'INR', order_id: '', event_source_url: '' });
  const [out, setOut] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!pixel && pixels.rows?.[0]) setPixel(pixels.rows[0].id);
  }, [pixels.rows, pixel]);
  React.useEffect(() => {
    const row = (pixels.rows || []).find((p) => p.id === pixel);
    if (row?.test_event_code) setTestCode(row.test_event_code);
  }, [pixel, pixels.rows]);
  const send = async () => {
    setBusy(true);
    setOut(null);
    try {
      const data = await postJSON(`${SB_API}/test-event/`, {
        pixel_account: pixel,
        event_type: eventType,
        incoming_event: incomingId || undefined,
        test_event_code: testCode,
        fields,
      });
      setOut(data);
      if (onSent) onSent(data);
    } catch (err) {
      setOut({ error: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div style={{ display: 'grid', gap: 10, maxWidth: 560 }}>
        <label>Pixel
          <select value={pixel} onChange={(e) => setPixel(e.target.value)} style={{ width: '100%' }}>
            {(pixels.rows || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Test event code
          <input
            value={testCode}
            onChange={(e) => setTestCode(e.target.value)}
            placeholder="TEST12345"
            style={{ width: '100%' }}
          />
        </label>
        <label>Event type
          <select value={eventType} onChange={(e) => setEventType(e.target.value)} style={{ width: '100%' }}>
            {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase', 'Refund', 'Cancellation'].map((e) => <option key={e}>{e}</option>)}
          </select>
        </label>
        <label>Or replay a recent incoming event
          <select value={incomingId} onChange={(e) => setIncomingId(e.target.value)} style={{ width: '100%' }}>
            <option value="">— build from the form —</option>
            {(incoming.rows || []).map((r) => (
              <option key={r.id} value={r.id}>{r.source} {r.id.slice(0, 8)}…</option>
            ))}
          </select>
        </label>
        {!incomingId && ['email', 'phone', 'value', 'currency', 'order_id', 'event_source_url'].map((k) => (
          <label key={k}>{k}
            <input value={fields[k] || ''} onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))} style={{ width: '100%' }} />
          </label>
        ))}
        <button type="button" className="ty-btn" disabled={busy || !pixel} onClick={send}>
          {busy ? 'Sending…' : 'Send test event'}
        </button>
      </div>
      {out && (
        <div style={{ marginTop: 16 }}>
          <p>
            {out.error
              ? 'Test did not send.'
              : 'Test sent. Open Events Manager → Test Events and filter by the code you pasted. The pixel record was not edited.'}
          </p>
          <TechDetails title="Result" data={out} />
        </div>
      )}
    </div>
  );
}

export function SignalBridgeTestEvent() {
  return (
    <UniPageShell
      {...P} icon="⚗" title="Send a test event" helpPage="test_event" helpOpen={false}
      subtitle="Paste a Test Events code for this send only — the pixel record is not edited. Open Events Manager → Test Events to confirm."
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <TestEventForm />
    </UniPageShell>
  );
}

export function SignalBridgeResend() {
  const [since, setSince] = React.useState('');
  const [until, setUntil] = React.useState('');
  const [eventType, setEventType] = React.useState('');
  const [st, setSt] = React.useState('');
  const qs = [
    since && `since=${encodeURIComponent(since)}`,
    until && `until=${encodeURIComponent(until)}`,
    eventType && `event_type=${encodeURIComponent(eventType)}`,
    st && `status=${encodeURIComponent(st)}`,
    'page_size=100',
  ].filter(Boolean).join('&');
  const list = useUniList(`${SB_API}/events/?${qs}`, [qs]);
  const [selected, setSelected] = React.useState({});
  const [confirm, setConfirm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const ids = Object.keys(selected).filter((k) => selected[k]);
  const preview = async () => {
    const data = await postJSON(`${SB_API}/events/resend/`, { ids, confirm: false });
    setConfirm(data);
  };
  const run = async () => {
    setBusy(true);
    try {
      const data = await postJSON(`${SB_API}/events/resend/`, { ids, confirm: true });
      setConfirm({ done: data });
      list.reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <UniPageShell
      {...P} icon="↻" title="Historical resend" helpPage="resend"
      subtitle="Events older than 7 days are auto-skipped. Timestamps are never rewritten."
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All types</option>
          {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase', 'Refund', 'Cancellation'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={st} onChange={(e) => setSt(e.target.value)}>
          <option value="">All statuses</option>
          {['PENDING', 'SENT', 'FAILED', 'REJECTED', 'SKIPPED_TOO_OLD', 'SKIPPED_NO_CLICK_ID', 'PARTIAL'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <button type="button" className="ty-btn" disabled={!ids.length} onClick={preview}>
          Resend selected ({ids.length})
        </button>
      </div>
      <UniTable
        rows={list.rows} loading={list.loading} error={list.error} rowKey="id"
        empty="No events"
        columns={[
          { key: 'sel', label: '', render: (r) => (
            <input type="checkbox" checked={!!selected[r.id]} onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))} />
          ) },
          { key: 'event_id', label: 'Event id' },
          { key: 'event_type', label: 'Type' },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          { key: 'event_time', label: 'event_time' },
        ]}
      />
      {confirm && (
        <div className="uni-note" style={{ marginTop: 12 }}>
          {confirm.needs_confirm && (
            <>
              <p>{confirm.note}</p>
              <p>Will send: <strong>{confirm.will_send}</strong> · Skip too old: <strong>{confirm.will_skip_too_old}</strong></p>
              <button type="button" className="ty-btn" disabled={busy} onClick={run}>
                {busy ? 'Resending…' : 'Confirm resend'}
              </button>
            </>
          )}
          {confirm.done && <pre>{JSON.stringify(confirm.done, null, 2)}</pre>}
        </div>
      )}
    </UniPageShell>
  );
}

export function SignalBridgeUpload() {
  const stores = useUniList(`${SB_API}/stores/`);
  const batches = useUniList(`${SB_API}/uploads/`);
  const [store, setStore] = React.useState('');
  const [fmt, setFmt] = React.useState('GENERIC');
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const nav = useNavigate();
  const downloadTpl = (f) => downloadFile(`${SB_API}/templates/?format=${f}`, `signalbridge-${f.toLowerCase()}.csv`);
  const firstPass = async () => {
    if (!file || !store) return;
    setBusy(true);
    try {
      const data = await uploadFile(`${SB_API}/uploads/`, file, {
        store_connection: store,
        platform_format: fmt,
        confirm: 'false',
      });
      setPreview(data);
    } catch (err) {
      setPreview({ error: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!file || !store) return;
    setBusy(true);
    try {
      const data = await uploadFile(`${SB_API}/uploads/`, file, {
        store_connection: store,
        platform_format: fmt,
        confirm: 'true',
      });
      nav(`/signalbridge/uploads/${data.id}`);
    } catch (err) {
      setPreview({ error: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <UniPageShell
      {...P} icon="⬆" title="CSV / Excel upload" helpPage="upload"
      subtitle="Rows older than 7 days are counted and never sent. Timestamps are not rewritten."
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => downloadTpl('GENERIC')}>Generic template</button>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => downloadTpl('SHOPIFY_EXPORT')}>Shopify export template</button>
        <button type="button" className="ty-btn ty-btn--secondary" onClick={() => downloadTpl('GOKWIK_EXPORT')}>GoKwik template</button>
      </div>
      <div style={{ display: 'grid', gap: 10, maxWidth: 520 }}>
        <label>Store
          <select value={store} onChange={(e) => setStore(e.target.value)} style={{ width: '100%' }}>
            <option value="">—</option>
            {(stores.rows || []).map((s) => <option key={s.id} value={s.id}>{s.store_domain}</option>)}
          </select>
        </label>
        <label>Format
          <select value={fmt} onChange={(e) => setFmt(e.target.value)} style={{ width: '100%' }}>
            <option value="GENERIC">Generic</option>
            <option value="SHOPIFY_EXPORT">Shopify export</option>
            <option value="GOKWIK_EXPORT">GoKwik export</option>
          </select>
        </label>
        <input type="file" accept=".csv,.xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button type="button" className="ty-btn" disabled={busy || !file || !store} onClick={firstPass}>
          {busy ? 'Reading…' : 'Review counts'}
        </button>
      </div>
      {preview && (
        <div className="uni-note" style={{ marginTop: 16 }}>
          {preview.needs_confirm ? (
            <>
              <p>{preview.note}</p>
              <p>
                Total {preview.total_rows} · valid {preview.valid_rows} ·
                skip too old <strong>{preview.will_skip_too_old}</strong> ·
                invalid {preview.invalid_rows}
              </p>
              <button type="button" className="ty-btn" disabled={busy} onClick={confirm}>
                Confirm and process
              </button>
            </>
          ) : (
            <pre>{JSON.stringify(preview, null, 2)}</pre>
          )}
        </div>
      )}
      <UniSection title="Recent batches" />
      <UniTable
        rows={batches.rows} loading={batches.loading} rowKey="id"
        empty="No uploads yet"
        columns={[
          { key: 'original_filename', label: 'File', render: (r) => <Link to={`/signalbridge/uploads/${r.id}`}>{r.original_filename || r.id}</Link> },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          { key: 'total_rows', label: 'Rows', align: 'right' },
          { key: 'sent_rows', label: 'Sent', align: 'right' },
          { key: 'skipped_old_rows', label: 'Too old', align: 'right' },
          { key: 'created_at', label: 'Created' },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeBatchDetail() {
  const { id } = useParams();
  const detail = useUniList(id ? `${SB_API}/uploads/${id}/rows/` : '', [id]);
  const batch = detail.raw?.batch || {};
  return (
    <UniPageShell
      {...P} icon="📄" title="Upload batch" helpPage="batch"
      subtitle={batch.original_filename}
      actions={(
        <>
          <UniActionButton hero onClick={() => downloadFile(`${SB_API}/uploads/${id}/error-report/`, `batch-${id}-errors.csv`)}>
            Error CSV
          </UniActionButton>
          <UniActionButton hero to="/signalbridge/upload">← Uploads</UniActionButton>
        </>
      )}
    >
      {batch.skipped_old_rows > 0 && (
        <div className="uni-note" style={{ marginBottom: 16 }}>
          <strong>{batch.skipped_old_rows}</strong> row(s) were older than Meta's 7-day
          window. They were stored as SKIPPED_TOO_OLD and were not sent.
          SignalBridge does not change event_time.
        </div>
      )}
      <div className="uni-kpis">
        <UniKpiCard label="Total" value={fmtCount(batch.total_rows)} />
        <UniKpiCard label="Valid" value={fmtCount(batch.valid_rows)} />
        <UniKpiCard label="Sent" value={fmtCount(batch.sent_rows)} />
        <UniKpiCard label="Failed" value={fmtCount(batch.failed_rows)} />
        <UniKpiCard label="Too old" value={fmtCount(batch.skipped_old_rows)} />
      </div>
      <UniTable
        rows={detail.raw?.rows || []} loading={detail.loading} rowKey="row_number"
        empty="No rows"
        columns={[
          { key: 'row_number', label: 'Row' },
          { key: 'event_id', label: 'Event id' },
          { key: 'outbound_status', label: 'Status', render: (r) => <UniStatusPill value={r.outbound_status || r.incoming_status} /> },
          { key: 'error', label: 'Error' },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [since, setSince] = React.useState('');
  const [until, setUntil] = React.useState('');
  const [eventType, setEventType] = React.useState('');
  const [payment, setPayment] = React.useState('');
  const [st, setSt] = React.useState('');
  const [source, setSource] = React.useState('');
  const [qDraft, setQDraft] = React.useState(() => (searchParams.get('search') || '').trim());
  const [q, setQ] = React.useState(() => (searchParams.get('search') || '').trim());
  const [ordering, setOrdering] = React.useState('-event_time');
  const [page, setPage] = React.useState(1);
  const tz = useDisplayTimezone();
  React.useEffect(() => {
    const t = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(t);
  }, [qDraft]);
  React.useEffect(() => {
    setSearchParams((prev) => {
      const current = (prev.get('search') || '').trim();
      if (current === q) return prev;
      const next = new URLSearchParams(prev);
      if (q) next.set('search', q);
      else next.delete('search');
      return next;
    }, { replace: true });
  }, [q, setSearchParams]);
  const applySearch = () => setQ(qDraft.trim());
  const clearSearch = () => {
    setQDraft('');
    setQ('');
  };
  const filterKey = [since, until, eventType, payment, st, source, q, ordering].join('|');
  React.useEffect(() => { setPage(1); }, [filterKey]);
  const filterQs = [
    since && `since=${encodeURIComponent(since)}`,
    until && `until=${encodeURIComponent(until)}`,
    eventType && `event_type=${encodeURIComponent(eventType)}`,
    payment && `payment_type=${encodeURIComponent(payment)}`,
    st && `status=${encodeURIComponent(st)}`,
    source && `source=${encodeURIComponent(source)}`,
    q && `search=${encodeURIComponent(q)}`,
    ordering && `ordering=${encodeURIComponent(ordering)}`,
  ].filter(Boolean).join('&');
  const qs = [filterQs, `page=${page}`, 'page_size=100'].filter(Boolean).join('&');
  const list = useUniList(`${SB_API}/events/?${qs}`, [qs]);
  const exportCsv = () => downloadFile(`${SB_API}/events/export/?${filterQs}`, 'signalbridge-events.csv');
  const headerSort = headerSortFromOrdering(ordering, EVENT_ORDER_TO_COL);
  return (
    <UniPageShell
      {...P} icon="📋" title="Event log" helpPage="events" helpApi={SB_API}
      subtitle={`Order time is the Shopify timestamp in ${tz}. Organisation admins change it in Business → Timezone.`}
      actions={(
        <>
          <UniActionButton hero to="/signalbridge/settings">Timezone</UniActionButton>
          <UniActionButton hero to="/signalbridge/payloads">Meta payloads</UniActionButton>
          <UniActionButton hero onClick={exportCsv}>Export CSV</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <StoreOrderSyncPanel onSynced={() => { if (list.reload) list.reload(); }} />
      <form
        className="sb-event-search"
        onSubmit={(e) => { e.preventDefault(); applySearch(); }}
      >
        <EasyField
          label="Search events"
          hint="Shopify order number (#3210), event id, or GoKwik cart token"
        >
          <input
            type="search"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="#3210"
            autoComplete="off"
            aria-label="Search events"
          />
        </EasyField>
        <UniActionButton hero primary onClick={applySearch}>Search</UniActionButton>
        {q ? (
          <UniActionButton hero onClick={clearSearch}>Clear</UniActionButton>
        ) : null}
      </form>
      {q ? (
        <p className="uni-num--muted" style={{ margin: '0 0 12px' }}>
          Showing matches for <strong>{q}</strong>
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All types</option>
          {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase', 'Refund', 'Cancellation'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={payment} onChange={(e) => setPayment(e.target.value)}>
          <option value="">All payments</option>
          {['PREPAID', 'COD', 'UNKNOWN', 'N/A'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={st} onChange={(e) => setSt(e.target.value)}>
          <option value="">All statuses</option>
          {['PENDING', 'SENT', 'FAILED', 'REJECTED', 'SKIPPED_TOO_OLD', 'SKIPPED_NO_CLICK_ID', 'PARTIAL'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">All sources</option>
          {['SHOPIFY_WEBHOOK', 'GOKWIK_WEBHOOK', 'CSV_UPLOAD', 'MANUAL_TEST', 'BROWSER_COLLECT', 'API_INGEST'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
          <option value="-event_time">Order time newest</option>
          <option value="event_time">Order time oldest</option>
          <option value="-created_at">Newest first</option>
          <option value="created_at">Oldest first</option>
          <option value="event_id">Event id A–Z</option>
          <option value="-event_id">Event id Z–A</option>
          <option value="order_number">Shopify order A–Z</option>
          <option value="-order_number">Shopify order Z–A</option>
          <option value="event_type">Event type A–Z</option>
          <option value="-event_type">Event type Z–A</option>
          <option value="payment_type">Payment A–Z</option>
          <option value="-payment_type">Payment Z–A</option>
          <option value="-value">Value high–low</option>
          <option value="value">Value low–high</option>
          <option value="status">Status A–Z</option>
          <option value="-status">Status Z–A</option>
          <option value="-attempts">Attempts high–low</option>
          <option value="attempts">Attempts low–high</option>
          <option value="-events_received_count">Received high–low</option>
          <option value="events_received_count">Received low–high</option>
          <option value="-sent_at">Sent newest</option>
          <option value="sent_at">Sent oldest</option>
          <option value="incoming_event__source">Source A–Z</option>
          <option value="-incoming_event__source">Source Z–A</option>
        </select>
      </div>
      <UniTable
        rows={list.rows} loading={list.loading} error={list.error} rowKey="id"
        empty="No events"
        sortKey={headerSort.key}
        sortDir={headerSort.dir}
        onSort={(key, dir) => {
          const api = EVENT_SORT_FIELDS[key];
          if (api) setOrdering(toOrderingParam(api, dir));
        }}
        columns={[
          { key: 'event_id', label: 'Event id', render: (r) => <Link to={`/signalbridge/events/${r.id}`}>{r.event_id}</Link> },
          { key: 'shopify_order_number', label: 'Shopify order', render: (r) => displayShopifyOrderNumber(r) },
          { key: 'event_time', label: 'Order time', render: (r) => formatInTimeZone(r.event_time || r.created_at, tz) },
          { key: 'event_type', label: 'Type', render: (r) => (r.is_abandoned ? `${r.event_type} · abandoned cart` : r.event_type) },
          { key: 'payment_type', label: 'Payment' },
          { key: 'value', label: 'Value', align: 'right' },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          { key: 'attempts', label: 'Attempts', align: 'right' },
          { key: 'events_received_count', label: 'Received', align: 'right' },
          { key: 'sent_at', label: 'Sent', render: (r) => formatInTimeZone(r.sent_at, tz) },
          { key: 'source', label: 'Source' },
        ]}
      />
      {list.raw && typeof list.raw.count === 'number' ? (
        <p className="uni-num--muted" style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            Showing {list.rows.length} of {fmtCount(list.raw.count)} events
            {list.raw.count > (list.rows.length || 0)
              ? ' — search by Shopify order number to find one order, or use Next for more pages.'
              : ''}
          </span>
          <UniActionButton hero disabled={page <= 1 || list.loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </UniActionButton>
          <UniActionButton hero disabled={!list.raw.next || list.loading} onClick={() => setPage((p) => p + 1)}>
            Next
          </UniActionButton>
        </p>
      ) : null}
    </UniPageShell>
  );
}

export function SignalBridgePayloads() {
  const pixels = useUniList(`${SB_API}/pixels/`);
  const [since, setSince] = React.useState('');
  const [until, setUntil] = React.useState('');
  const [eventType, setEventType] = React.useState('');
  const [st, setSt] = React.useState('');
  const [source, setSource] = React.useState('');
  const [pixel, setPixel] = React.useState('');
  const [isTest, setIsTest] = React.useState('');
  const [q, setQ] = React.useState('');
  const [ordering, setOrdering] = React.useState('-created_at');
  const [openId, setOpenId] = React.useState(null);
  const qs = [
    since && `since=${encodeURIComponent(since)}`,
    until && `until=${encodeURIComponent(until)}`,
    eventType && `event_type=${encodeURIComponent(eventType)}`,
    st && `status=${encodeURIComponent(st)}`,
    source && `source=${encodeURIComponent(source)}`,
    pixel && `pixel=${encodeURIComponent(pixel)}`,
    isTest && `is_test=${encodeURIComponent(isTest)}`,
    q && `search=${encodeURIComponent(q)}`,
    ordering && `ordering=${encodeURIComponent(ordering)}`,
  ].filter(Boolean).join('&');
  const list = useUniList(`${SB_API}/payloads/?${qs}`, [qs]);
  const headerSort = headerSortFromOrdering(ordering, PAYLOAD_ORDER_TO_COL);
  React.useEffect(() => {
    if (!openId) return undefined;
    const el = document.getElementById(`sb-payload-${openId}`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return undefined;
  }, [openId]);
  return (
    <UniPageShell
      {...P} icon="◈" title="Meta payloads" helpPage="payloads" helpApi={SB_API}
      actions={(
        <>
          <UniActionButton hero to="/signalbridge/templates">← Templates</UniActionButton>
          <UniActionButton hero to="/signalbridge/pixels">Datasets</UniActionButton>
          <UniActionButton hero to="/signalbridge/events">Event log</UniActionButton>
        </>
      )}
    >
      <p className="uni-num--muted">
        Every JSON body sent to Meta CAPI is kept here — Shopify webhooks, CAPI tests from Datasets, and other sources. Tokens are never stored.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All events</option>
          {['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase', 'Refund', 'Cancellation'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search event id or custom name"
        />
        <select value={st} onChange={(e) => setSt(e.target.value)}>
          <option value="">All statuses</option>
          {['PENDING', 'SENT', 'FAILED', 'REJECTED', 'SKIPPED_TOO_OLD', 'SKIPPED_NO_CLICK_ID'].map((e) => <option key={e}>{e}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">All sources</option>
          <option value="SHOPIFY_WEBHOOK">Shopify</option>
          <option value="GOKWIK_WEBHOOK">GoKwik</option>
          <option value="CSV_UPLOAD">CSV upload</option>
          <option value="MANUAL_TEST">Manual test</option>
          <option value="BROWSER_COLLECT">Browser collect</option>
          <option value="API_INGEST">API</option>
        </select>
        <select value={pixel} onChange={(e) => setPixel(e.target.value)}>
          <option value="">All pixels</option>
          {(pixels.rows || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={isTest} onChange={(e) => setIsTest(e.target.value)}>
          <option value="">Live + tests</option>
          <option value="0">Live only</option>
          <option value="1">CAPI tests only</option>
        </select>
        <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
          <option value="-created_at">Newest first</option>
          <option value="created_at">Oldest first</option>
          <option value="outbound_event__event_type">Event type A–Z</option>
          <option value="-outbound_event__event_type">Event type Z–A</option>
          <option value="outbound_event__event_id">Event id A–Z</option>
          <option value="-outbound_event__event_id">Event id Z–A</option>
          <option value="outbound_event__pixel_account__name">Pixel A–Z</option>
          <option value="-outbound_event__pixel_account__name">Pixel Z–A</option>
          <option value="outbound_event__incoming_event__store_connection__store_domain">Store A–Z</option>
          <option value="-outbound_event__incoming_event__store_connection__store_domain">Store Z–A</option>
          <option value="outbound_event__incoming_event__source">Source A–Z</option>
          <option value="-outbound_event__incoming_event__source">Source Z–A</option>
          <option value="status">Status A–Z</option>
          <option value="-status">Status Z–A</option>
          <option value="-sent_at">Sent newest</option>
        </select>
      </div>
      <UniTable
        rows={list.rows} loading={list.loading} error={list.error} rowKey="id"
        empty="No Meta payloads yet. Shopify orders and CAPI tests from Datasets appear here after they are sent."
        sortKey={headerSort.key}
        sortDir={headerSort.dir}
        onSort={(key, dir) => {
          const api = PAYLOAD_SORT_FIELDS[key];
          if (api) setOrdering(toOrderingParam(api, dir));
        }}
        columns={[
          { key: 'created_at', label: 'When' },
          { key: 'event_type', label: 'Event' },
          { key: 'event_id', label: 'Event id', render: (r) => (r.outbound_id ? <Link to={`/signalbridge/events/${r.outbound_id}`}>{r.event_id}</Link> : r.event_id) },
          { key: 'pixel_name', label: 'Pixel' },
          { key: 'store_domain', label: 'Store' },
          { key: 'source', label: 'Source', render: (r) => r.source || (r.is_test ? 'TEST' : '—') },
          { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          {
            key: 'open',
            label: '',
            sortable: false,
            render: (r) => (
              <button
                type="button"
                className="ty-btn ty-btn--secondary"
                onClick={() => setOpenId(String(openId) === String(r.id) ? null : String(r.id))}
              >
                {String(openId) === String(r.id) ? 'Hide JSON' : 'View JSON'}
              </button>
            ),
          },
        ]}
        rowExpand={(r) => (
          String(openId) === String(r.id) ? (
            <pre id={`sb-payload-${r.id}`} className="uni-json">{payloadInspectJson(r)}</pre>
          ) : null
        )}
      />
    </UniPageShell>
  );
}

export function SignalBridgeEventDetail() {
  const { id } = useParams();
  const { raw, loading, error, reload } = useUniList(id ? `${SB_API}/events/${id}/` : '', [id]);
  const [open, setOpen] = React.useState(null);
  const [testCode, setTestCode] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [sendOut, setSendOut] = React.useState(null);
  const [syncOut, setSyncOut] = React.useState(null);
  const tz = useDisplayTimezone();
  const refreshFromShopify = async () => {
    setBusy('shopify');
    setSyncOut(null);
    try {
      const data = await postJSON(`${SB_API}/events/${id}/sync-from-shopify/`);
      setSyncOut(data);
      if (reload) reload();
    } catch (err) {
      const data = err && err.data;
      setSyncOut({
        ok: false,
        message: (data && (data.message || data.detail || data.error)) || String(err.message || err),
      });
    } finally {
      setBusy('');
    }
  };
  const refreshFromGokwik = async () => {
    setBusy('gokwik');
    setSyncOut(null);
    try {
      const data = await postJSON(`${SB_API}/events/${id}/sync-from-gokwik/`);
      setSyncOut(data);
      if (reload) reload();
    } catch (err) {
      const data = err && err.data;
      setSyncOut({
        ok: false,
        message: (data && (data.message || data.detail || data.error)) || String(err.message || err),
      });
    } finally {
      setBusy('');
    }
  };
  const sendToMeta = async (asTest) => {
    setBusy(asTest ? 'test' : 'live');
    setSendOut(null);
    try {
      const data = await postJSON(`${SB_API}/events/${id}/send-to-meta/`, asTest
        ? { as_test: true, test_event_code: testCode }
        : { as_test: false });
      setSendOut(data);
      if (reload) reload();
    } catch (err) {
      const data = err && err.data;
      const detail = data && (data.message || data.detail || data.error);
      setSendOut({
        ok: false,
        error: detail || String(err.message || err),
        message: detail || String(err.message || err) || 'This event did not reach Meta.',
        send_result: (data && data.send_result) || null,
      });
    } finally {
      setBusy('');
    }
  };
  return (
    <UniPageShell
      {...P} icon="📋" title="Event detail" helpPage="event_detail" helpApi={SB_API}
      actions={<UniActionButton hero to="/signalbridge/events">← Log</UniActionButton>}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {raw && (
        <>
          <p>
            <UniStatusPill value={raw.status} /> {raw.is_test ? 'TEST · ' : ''}{raw.event_type} · {raw.payment_type} ·
            Shopify order {displayShopifyOrderNumber(raw)} ·
            order time {formatInTimeZone(raw.event_time || raw.created_at, tz)} ·
            sent {formatInTimeZone(raw.sent_at, tz)} ·
            attempts {raw.attempts} · events_received {raw.events_received_count ?? '—'}
            {raw.meta_pixel_id ? ` · pixel ${raw.meta_pixel_id}` : ''}
          </p>
          {!sendOut && <MetaVisibilityNotice visibility={raw.meta_visibility} />}
          <UniSection title={raw.is_abandoned ? 'GoKwik abandoned cart' : (isGokwikEvent(raw) ? 'GoKwik order' : 'Shopify order')} />
          <p className="uni-num--muted">
            {raw.is_abandoned
              ? 'This row is a GoKwik abandoned cart sent to Meta as InitiateCheckout. Recovery URL, customer, address, and line items are stored for reporting. It is not posted again to Meta if it already sent.'
              : isGokwikEvent(raw)
              ? 'Pulls the live GoKwik JSON for this order and refreshes stored details (phone, payment, customer). Events already sent to Meta are not posted again.'
              : 'Pulls the live Admin JSON for this order and refreshes stored details (order name, payment, customer, click ids). Events already sent to Meta are not posted again.'}
          </p>
          {raw.is_abandoned && raw.abandon_url ? (
            <p>
              Recovery URL <CopyField compact value={raw.abandon_url} />
              {raw.checkout_id ? ` · cart ${raw.checkout_id}` : ''}
            </p>
          ) : null}
          {raw.is_abandoned ? null : (
          <div style={{ margin: '8px 0 16px' }}>
            {isGokwikEvent(raw) ? (
              <button
                type="button"
                className="ty-btn ty-btn--secondary"
                disabled={!!busy}
                onClick={refreshFromGokwik}
              >
                {busy === 'gokwik' ? 'Refreshing…' : 'Refresh from GoKwik'}
              </button>
            ) : (
              <button
                type="button"
                className="ty-btn ty-btn--secondary"
                disabled={!!busy}
                onClick={refreshFromShopify}
              >
                {busy === 'shopify' ? 'Refreshing…' : 'Refresh from Shopify'}
              </button>
            )}
          </div>
          )}
          {syncOut && (
            <div className={syncOut.ok === false ? 'ty-error' : 'ty-ok'} role="status" style={{ margin: '0 0 16px' }}>
              <div className="ty-error__head">
                <span className="ty-error__icon" aria-hidden="true">{syncOut.ok === false ? '⚠️' : '✓'}</span>
                {String(syncOut.message || syncOut.detail || (syncOut.ok === false ? 'Could not refresh this order.' : 'Refreshed from the store.'))}
              </div>
            </div>
          )}
          <UniSection title="Send to Meta" />
          <p className="uni-num--muted">
            Posts this event to Meta CAPI again (same event_id). A live resend is deduplicated
            and does not appear under Test Events — Overview / Diagnostics can take up to 20 minutes.
            A test send uses a Test Events code so this event_id shows in Events Manager immediately.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', margin: '8px 0 16px' }}>
            <button
              type="button"
              className="ty-btn"
              disabled={!!busy}
              onClick={() => sendToMeta(false)}
            >
              {busy === 'live' ? 'Sending…' : 'Send to Meta again'}
            </button>
            <label className="sb-test-aside__label" style={{ minWidth: 220 }}>
              Test event code
              <input
                value={testCode}
                onChange={(e) => setTestCode(e.target.value)}
                placeholder="TEST12345"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className="ty-btn ty-btn--secondary"
              disabled={!!busy}
              onClick={() => sendToMeta(true)}
            >
              {busy === 'test' ? 'Sending…' : 'Send as Meta test'}
            </button>
          </div>
          {sendOut && <SendToMetaResult sendOut={sendOut} />}
          <UniSection title="Deliveries (per destination)" />
          <UniTable
            rows={raw.deliveries || []}
            empty="No delivery attempts yet"
            rowKey="id"
            columns={[
              { key: 'destination_name', label: 'Destination' },
              { key: 'destination_type', label: 'Type' },
              { key: 'pixel_id', label: 'Pixel ID' },
              { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
              { key: 'response_status', label: 'HTTP' },
              { key: 'attempts', label: 'Attempts', align: 'right' },
              { key: 'latency_ms', label: 'Latency ms', align: 'right' },
              {
                key: 'open',
                label: '',
                render: (r) => (
                  <button type="button" className="ty-btn ty-btn--secondary" onClick={() => setOpen(String(open) === String(r.id) ? null : String(r.id))}>
                    {String(open) === String(r.id) ? 'Hide' : 'Payload'}
                  </button>
                ),
              },
            ]}
            rowExpand={(r) => (
              String(open) === String(r.id) ? (
                <pre className="uni-json">{payloadInspectJson(r)}</pre>
              ) : null
            )}
          />
          <UniSection title="Built payload (Meta CAPI JSON)" />
          <pre className="uni-json">{prettyJson(raw.built_payload)}</pre>
          <UniSection title="Normalized snapshot (unhashed internal JSON)" />
          <pre className="uni-json">{prettyJson(raw.normalized_snapshot)}</pre>
          <UniSection title="Meta response" />
          <pre className="uni-json">{prettyJson(raw.meta_response)}</pre>
          <UniSection title="Incoming raw payload" />
          <pre className="uni-json">{prettyJson(raw.incoming_raw_payload)}</pre>
        </>
      )}
    </UniPageShell>
  );
}

export function SignalBridgeHealth() {
  const { raw, loading, error } = useUniList(`${SB_API}/health/`);
  return (
    <UniPageShell
      {...P} icon="💚" title="Health" helpPage="health"
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <pre className="uni-note" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(raw, null, 2)}</pre>
    </UniPageShell>
  );
}

export function SignalBridgeHelp() {
  const help = useUniList(`${SB_API}/help/`);
  const features = useUniList(`${SB_API}/features/`);
  const pages = help.raw?.pages || {};
  const cats = features.raw?.categories || [];
  return (
    <UniPageShell
      {...P} icon="❓" title="SignalBridge help centre" helpPage="help"
      subtitle="The same copy as the banner on every SignalBridge screen, plus a feature list."
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      {help.loading && <div className="uni-state">Loading help…</div>}
      {help.error && <div className="ty-error">{String(help.error.message || help.error)}</div>}
      {Object.entries(pages).map(([key, item]) => (
        <article key={key} className="uni-note" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 6px' }}>{item.title} <span className="uni-num--muted">({key})</span></h3>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>{item.body}</p>
          {Array.isArray(item.steps) && item.steps.length > 0 && (
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
              {item.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
        </article>
      ))}
      {cats.map((c) => (
        <section key={c.category} className="uni-note" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 6px' }}>{c.category}</h3>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {(c.items || []).map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      ))}
    </UniPageShell>
  );
}
